/**
 * UPT-2079 F2 (web §5.3) — modèle PUR de l'éditeur de menu : groupes (`navigation.menus[]`), éléments (objet, page, tableau de bord du
 * module, séparateur), icônes du catalogue. Chaque opération travaille sur une COPIE du document (celle que le réducteur a clonée) et
 * rend `false` quand elle n'a aucun effet (cible inconnue, borne, doublon) : le réducteur rend alors le même état. Les propriétés v1.3
 * (icône, séparateur, tableau de bord) portent `specVersion` à « 1.3 » ; un menu ne garde jamais de séparateur en tête, en queue ou
 * doublé, et un groupe vidé disparaît (le schéma exige au moins un élément).
 */
import type { DesignModuleSpecJson } from '../data/contracts';
import { navIconComponent } from '@/uptk/core/components/common/navIcons';
import { asRecord, asRecords, asStrings, frText, type I18nText, type Json } from './specTypes';

export const NAVIGATION_LIMITS = { menus: 12, items: 120, labelMaxChars: 120 } as const;
const KEY_PATTERN = /^[a-z][a-z0-9-]*$/;

/** Une cible d'élément de menu. */
export type NavigationTarget = { entity: string } | { page: string } | { dashboard: true } | { separator: true };

export interface NavigationItemPatch {
  /** Libellé par langue (`fr` obligatoire) ; `null` retire le libellé (le serveur prend alors celui de l'objet ou de la page). */
  label?: I18nText | null;
  /** Clé du catalogue (`icons.heroicons`) ; `null` retire l'icône. */
  icon?: string | null;
  portals?: string[] | null;
}
export interface NavigationGroupPatch extends NavigationItemPatch {
  key?: string;
  order?: number | null;
}

/** Document de navigation lu de façon défensive. */
export const readMenus = (spec: DesignModuleSpecJson): Json[] => asRecords(asRecord(spec.navigation)?.menus);

const menusOf = (spec: DesignModuleSpecJson): Json[] | null => {
  const navigation = asRecord(spec.navigation);
  if (!navigation || !Array.isArray(navigation.menus)) return null;
  return navigation.menus as Json[];
};

const ensureMenus = (spec: DesignModuleSpecJson): Json[] => {
  const navigation: Json = asRecord(spec.navigation) ?? {};
  if (!Array.isArray(navigation.menus)) navigation.menus = [];
  spec.navigation = navigation;
  return navigation.menus as Json[];
};

const itemsOf = (menu: Json | undefined): Json[] => (menu && Array.isArray(menu.items) ? (menu.items as Json[]) : []);
const isSeparator = (item: Json | undefined): boolean => item?.separator === true;
const countItems = (spec: DesignModuleSpecJson): number => readMenus(spec).reduce((sum, menu) => sum + itemsOf(menu).length, 0);

const sameTarget = (item: Json, target: NavigationTarget): boolean =>
  ('entity' in target && item.entity === target.entity) || ('page' in target && item.page === target.page) || ('dashboard' in target && item.dashboard === true);

/** Vrai si le document place déjà cette cible (objet, page, tableau de bord) dans un groupe. */
export const hasTarget = (spec: DesignModuleSpecJson, target: NavigationTarget): boolean =>
  'separator' in target ? false : readMenus(spec).some((menu) => itemsOf(menu).some((item) => sameTarget(item, target)));

/** Vrai si la navigation porte une propriété du schéma v1.3 (icône, séparateur, tableau de bord). */
export const navigationUsesV13 = (navigation: unknown): boolean =>
  asRecords(asRecord(navigation)?.menus).some((menu) => 'icon' in menu || itemsOf(menu).some((item) => 'icon' in item || 'separator' in item || 'dashboard' in item));

/** Porte `specVersion` à « 1.3 » dès qu'une propriété v1.3 est présente (le serveur le ferait de toute façon : `spec.version_bumped`). */
export const bumpNavigationVersion = (spec: DesignModuleSpecJson): void => {
  if (navigationUsesV13(spec.navigation) && !['1.3','1.4'].includes(String(spec.specVersion))) spec.specVersion = '1.3';
};

/** Retire les séparateurs en tête, en queue ou doublés d'un groupe. */
const cleanSeparators = (menu: Json): void => {
  const out: Json[] = [];
  for (const item of itemsOf(menu)) {
    if (isSeparator(item) && (out.length === 0 || isSeparator(out[out.length - 1]))) continue;
    out.push(item);
  }
  while (out.length > 0 && isSeparator(out[out.length - 1])) out.pop();
  menu.items = out;
};

/** Normalise toute la navigation : séparateurs propres, groupes vides retirés, section vidée retirée. */
export const normalizeNavigation = (spec: DesignModuleSpecJson): void => {
  const navigation = asRecord(spec.navigation);
  if (!navigation) return;
  const kept: Json[] = [];
  for (const menu of asRecords(navigation.menus)) {
    cleanSeparators(menu);
    if (itemsOf(menu).length > 0) kept.push(menu);
  }
  if (kept.length > 0) navigation.menus = kept;
  else delete navigation.menus;
  if (Object.keys(navigation).length === 0) delete spec.navigation;
};

const slug = (text: string): string => {
  const folded = text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return KEY_PATTERN.test(folded) ? folded.slice(0, 40).replace(/-+$/, '') : 'menu';
};

const uniqueKey = (menus: Json[], base: string, ignore?: Json): string => {
  const taken = new Set(menus.filter((menu) => menu !== ignore).map((menu) => menu.key));
  let candidate = base;
  for (let n = 2; taken.has(candidate); n += 1) candidate = `${base}-${n}`;
  return candidate;
};

const cleanLabel = (label: I18nText | null | undefined): I18nText | null => {
  if (label === null || label === undefined) return null;
  if (typeof label === 'string') return null;
  const out: Record<string, string> = {};
  for (const [language, value] of Object.entries(label)) {
    if (typeof value === 'string' && value.trim() !== '') out[language] = value.trim().slice(0, NAVIGATION_LIMITS.labelMaxChars);
  }
  return typeof out.fr === 'string' ? out : null;
};

const applyPatch = (target: Json, patch: NavigationGroupPatch | NavigationItemPatch, isGroup: boolean): boolean => {
  const before = JSON.stringify(target);
  if ('label' in patch) {
    const label = cleanLabel(patch.label);
    if (label) target.label = label;
    else if (!isGroup) delete target.label;
  }
  if ('icon' in patch) {
    if (patch.icon === null || patch.icon === undefined || patch.icon === '') delete target.icon;
    else if (navIconComponent(patch.icon)) target.icon = patch.icon;
  }
  if ('portals' in patch) {
    const portals = asStrings(patch.portals ?? []).filter((portal, index, all) => ['portal', 'adminportal'].includes(portal) && all.indexOf(portal) === index);
    if (portals.length > 0) target.portals = portals;
    else delete target.portals;
  }
  if (isGroup && 'order' in patch) {
    const order = (patch as NavigationGroupPatch).order;
    if (typeof order === 'number' && Number.isInteger(order)) target.order = order;
    else delete target.order;
  }
  return JSON.stringify(target) !== before;
};

/** Premier objet, page ou le tableau de bord que le menu ne place pas encore (pour amorcer un nouveau groupe). */
export const firstFreeTarget = (spec: DesignModuleSpecJson): NavigationTarget | null => {
  for (const entity of Object.keys(asRecord(spec.entities) ?? {})) if (!hasTarget(spec, { entity })) return { entity };
  for (const page of asRecords(spec.pages)) if (typeof page.key === 'string' && !hasTarget(spec, { page: page.key })) return { page: page.key };
  return hasTarget(spec, { dashboard: true }) ? null : { dashboard: true };
};

const targetItem = (spec: DesignModuleSpecJson, target: NavigationTarget): Json | null => {
  if ('separator' in target) return { separator: true };
  if ('dashboard' in target) return hasTarget(spec, target) ? null : { dashboard: true };
  if ('entity' in target) return asRecord(spec.entities)?.[target.entity] && !hasTarget(spec, target) ? { entity: target.entity } : null;
  return asRecords(spec.pages).some((page) => page.key === target.page) && !hasTarget(spec, target) ? { page: target.page } : null;
};

export const groupAdd = (spec: DesignModuleSpecJson, label: string): boolean => {
  const menus = ensureMenus(spec);
  if (menus.length >= NAVIGATION_LIMITS.menus || countItems(spec) >= NAVIGATION_LIMITS.items) return false;
  const seed = firstFreeTarget(spec);
  const item = seed ? targetItem(spec, seed) : null;
  if (!item) return false;
  const text = label.trim().slice(0, NAVIGATION_LIMITS.labelMaxChars) || 'Menu';
  menus.push({ key: uniqueKey(menus, slug(text)), label: { fr: text }, items: [item] });
  return true;
};

export const groupUpdate = (spec: DesignModuleSpecJson, index: number, patch: NavigationGroupPatch): boolean => {
  const menus = menusOf(spec);
  const menu = menus?.[index];
  if (!menus || !menu) return false;
  let changed = false;
  if (typeof patch.key === 'string' && patch.key !== menu.key && KEY_PATTERN.test(patch.key) && !menus.some((other) => other !== menu && other.key === patch.key)) {
    menu.key = patch.key;
    changed = true;
  }
  return applyPatch(menu, patch, true) || changed;
};

export const groupRemove = (spec: DesignModuleSpecJson, index: number): boolean => {
  const menus = menusOf(spec);
  if (!menus || !menus[index]) return false;
  menus.splice(index, 1);
  return true;
};

export const groupMove = (spec: DesignModuleSpecJson, from: number, to: number): boolean => {
  const menus = menusOf(spec);
  if (!menus || from === to || !menus[from] || to < 0 || to >= menus.length) return false;
  const [moved] = menus.splice(from, 1);
  menus.splice(to, 0, moved);
  return true;
};

export const itemAdd = (spec: DesignModuleSpecJson, group: number, target: NavigationTarget, at?: number): boolean => {
  const menus = menusOf(spec);
  const menu = menus?.[group];
  if (!menu || countItems(spec) >= NAVIGATION_LIMITS.items) return false;
  const item = targetItem(spec, target);
  if (!item) return false;
  const items = itemsOf(menu);
  const index = typeof at === 'number' && at >= 0 && at <= items.length ? at : items.length;
  if ('separator' in target && (index === 0 || index >= items.length || isSeparator(items[index - 1]) || isSeparator(items[index]))) return false;
  items.splice(index, 0, item);
  menu.items = items;
  return true;
};

export const itemUpdate = (spec: DesignModuleSpecJson, group: number, index: number, patch: NavigationItemPatch): boolean => {
  const item = itemsOf(menusOf(spec)?.[group])[index];
  if (!item || isSeparator(item)) return false;
  return applyPatch(item, patch, false);
};

export const itemRemove = (spec: DesignModuleSpecJson, group: number, index: number): boolean => {
  const menu = menusOf(spec)?.[group];
  const items = itemsOf(menu);
  if (!menu || !items[index]) return false;
  items.splice(index, 1);
  menu.items = items;
  return true;
};

/** Déplace un élément dans son groupe (`toGroup` absent) ou vers la fin / une place d'un autre groupe. */
export const itemMove = (spec: DesignModuleSpecJson, group: number, from: number, to: number, toGroup: number = group): boolean => {
  const menus = menusOf(spec);
  const source = menus?.[group];
  const target = menus?.[toGroup];
  if (!menus || !source || !target) return false;
  const sourceItems = itemsOf(source);
  const moved = sourceItems[from];
  if (!moved || (group === toGroup && (from === to || to < 0 || to >= sourceItems.length))) return false;
  if (group !== toGroup && isSeparator(moved)) return false;
  sourceItems.splice(from, 1);
  const targetItems = group === toGroup ? sourceItems : itemsOf(target);
  const index = Math.max(0, Math.min(to, targetItems.length));
  targetItems.splice(index, 0, moved);
  source.items = sourceItems;
  target.items = targetItems;
  return true;
};

/** Remplace la navigation par celle que le serveur propose (menus et accueil) ; l'accueil n'est gardé que s'il existe encore. */
export const navigationReplace = (spec: DesignModuleSpecJson, proposed: unknown, addedPages?: unknown): boolean => {
  const menus = asRecords(asRecord(proposed)?.menus).filter((menu) => typeof menu.key === 'string' && itemsOf(menu).length > 0);
  if (menus.length === 0 || menus.length > NAVIGATION_LIMITS.menus) return false;
  const pages = asRecords(spec.pages);
  const additions: Json[] = [];
  if (addedPages !== undefined && !Array.isArray(addedPages)) return false;
  for (const page of asRecords(addedPages)) {
    if (typeof page.key !== 'string' || !KEY_PATTERN.test(page.key) || page.kind !== 'custom' || !frText(page.label, '')) return false;
    const existing = [...pages, ...additions].find(p => p.key === page.key);
    if (existing) {
      if (JSON.stringify(existing) !== JSON.stringify(page)) return false;
    } else additions.push(JSON.parse(JSON.stringify(page)) as Json);
  }
  const knownPages = new Set([...pages, ...additions].map(page => page.key));
  if (menus.some(menu => itemsOf(menu).some(item => typeof item.page === 'string' && !knownPages.has(item.page)))) return false;
  const previous = asRecord(spec.navigation);
  const navigation: Json = { menus: JSON.parse(JSON.stringify(menus)) as Json[] };
  // Recette Change Management (25.10.2026) : l'ancienne page d'accueil n'est gardée que si elle n'est pas un aperçu fidèle (jamais livré).
  const previousHome = asRecords(spec.pages).some(page => page.key === previous?.homePage && asRecord(page.faithful)) ? undefined : previous?.homePage;
  const home = asRecord(proposed)?.homePage ?? previousHome;
  const known = typeof home === 'string' && (Boolean(asRecord(spec.entities)?.[home]) || asRecords(spec.pages).some((page) => page.key === home));
  if (known) navigation.homePage = home;
  spec.navigation = navigation;
  if (additions.length) spec.pages = [...pages, ...additions];
  return true;
};

/** Retire une cible (objet, page) de tous les groupes — cascade de la suppression d'un objet ou d'une page. */
export const dropTarget = (spec: DesignModuleSpecJson, target: { entity?: string; page?: string }): void => {
  const menus = menusOf(spec);
  if (!menus) return;
  for (const menu of menus) {
    menu.items = itemsOf(menu).filter((item) => !(target.entity && item.entity === target.entity) && !(target.page && item.page === target.page));
  }
  normalizeNavigation(spec);
};

/** Libellé affiché d'un groupe (repli : la clé). */
export const groupLabel = (menu: Json): string => frText(menu.label, typeof menu.key === 'string' ? menu.key : '');
